// src/components/training/WeeklySummaryList.tsx
import React, { useEffect, useState, useCallback, ChangeEvent } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/configureStore";
import { loadWeeklySummariesForDateRange } from "../../actions/weeklySummaryActions";
import TrainingListLabelInput from "../training/TrainingListLabelInput";
import { SummaryDTO } from "../../types/SummaryDTO";

import SummaryRow from "./summary/SummaryRow";
import SummaryTableHeader from "./summary/SummaryTableHeader";
import { formatDateLocal, HR_DOMAIN_ORDER, HR_ZONE_ORDER, parseIsoDateUtc } from "./summary/summaryFormat";

interface ListingDates {
  listSince: Date;
  listTo: Date;
}

function WeeklySummaryList() {
  const dispatch = useDispatch<AppDispatch>();
  const userStore = useSelector((state: RootState) => state.loggedInUser);
  const weeklySummaries = useSelector((state: RootState) => state.weeklySummaryList.summaries);
  const error = useSelector((state: RootState) => state.weeklySummaryList.error);

  const [showHrZones, setShowHrZones] = useState<boolean>(true);

  const safeSummaries: SummaryDTO[] = Array.isArray(weeklySummaries) ? weeklySummaries : [];

  const initEndDate = new Date();
  initEndDate.setUTCHours(0, 0, 0, 0);
  const initStartDate = new Date(initEndDate.getTime() - 8 * 7 * 24 * 60 * 60 * 1000);

  const [listingDates, setListingDates] = useState<ListingDates>({
    listSince: initStartDate,
    listTo: initEndDate,
  });

  const loadSummaries = useCallback(() => {
    if (userStore?.id) {
      dispatch(loadWeeklySummariesForDateRange(userStore.id, listingDates.listSince, listingDates.listTo));
    }
  }, [dispatch, userStore, listingDates.listSince, listingDates.listTo]);

  useEffect(() => {
    loadSummaries();
  }, [loadSummaries]);

  const handleListingDate = (event: ChangeEvent<HTMLInputElement>) => {
    const { id, value } = event.target;
    if (!value) return;

    setListingDates((prev) => ({
      ...prev,
      [id === "weeklySummaryListSince" ? "listSince" : "listTo"]: parseIsoDateUtc(value),
    }));
  };

  return (
    <div className="card my-2">
      <div className="card-header d-flex flex-wrap align-items-center gap-2">
        <div className="flex-grow-0">Podsumowania tygodniowe</div>

        <div className="d-flex flex-wrap align-items-center">
          <TrainingListLabelInput
            label="Od:"
            id="weeklySummaryListSince"
            type="date"
            value={formatDateLocal(listingDates.listSince)}
            onChange={handleListingDate}
            placeholder="Od daty"
          />
          <TrainingListLabelInput
            label="Do:"
            id="weeklySummaryListTo"
            type="date"
            value={formatDateLocal(listingDates.listTo)}
            onChange={handleListingDate}
            placeholder="Do daty"
          />

          <div className="form-check form-switch ms-3 d-flex align-items-center">
            <input
              className="form-check-input"
              type="checkbox"
              id="weeklyToggleHrZones"
              checked={showHrZones}
              onChange={(e) => setShowHrZones(e.target.checked)}
            />
            <label className="form-check-label ms-2" htmlFor="weeklyToggleHrZones">
              {showHrZones ? "HR Zones" : "Stats"}
            </label>
          </div>
        </div>
      </div>

      <div className="card-body">
        {error && <div className="alert alert-danger">Error: {error}</div>}

        <div className="table-responsive">
          <table className="table table-striped table-hover">
            <SummaryTableHeader showHrZones={showHrZones} />

            <tbody>
              {safeSummaries.map((s) => (
                <SummaryRow
                  key={s.key ?? String(s.weekStartDate)}
                  summary={s}
                  showHrZones={showHrZones}
                  hrZoneOrder={HR_ZONE_ORDER}
                  domainOrder={HR_DOMAIN_ORDER}
                />
              ))}

              {safeSummaries.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ opacity: 0.7 }}>
                    Brak danych w wybranym zakresie.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default WeeklySummaryList;